import client from 'core/axios';
import handleError from 'core/axios/handleError';
import {
  handleJSONResponse,
  handleFileResponse,
} from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

import { convertToQueryStage } from 'modules/CollegeApplicationManager/utils';

const cleanParams = (params = {}) => {
  const { stage, ...rest } = params;

  if (stage) {
    rest.stage = convertToQueryStage(stage);
  }

  return rest;
};

const FORM_TYPE_BASE_PATH = Object.freeze({
  'school-profile': 'school-profile-forms',
  'counselor-profile': 'counselor-profile-forms',
  'teacher-profile': 'teacher-profile-forms',
});

const getFormBasePath = (version, formType) => {
  const path = FORM_TYPE_BASE_PATH[formType] ?? 'forms';
  return `${version}/app-manager/${path}/`;
};

const getFormDataParams = (params, formType) => {
  if (formType in FORM_TYPE_BASE_PATH) {
    return null;
  }
  return cleanParams(params);
};

export const getApplicationList = ({
  params = { limit: 30, offset: 0 },
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/app-manager/document-manager/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationCount = ({ params = {}, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/document-management-counts/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationUnsentCount = ({ params = {}, version = 'v3' }) =>
  client
    .request({
      url: `${version}/app-manager/document-manager/unsent-count/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getBulkPrintDocuments = ({ params = {}, version = 'v3' }) =>
  client
    .request({
      url: `${version}/app-manager/document-manager/print-documents/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getBulkSendDocuments = ({ params = {}, version = 'v3' }) =>
  client
    .request({
      url: `${version}/app-manager/document-manager/send-documents/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const bulkSendDocuments = ({ params = {}, version = 'v3' }) =>
  client
    .request({
      url: `${version}/app-manager/document-manager/send-documents/`,
      method: RequestMethod.POST,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRequirementList = ({
  params = { limit: 30, offset: 0 },
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/requirements/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const printApplicationDocuments = ({
  applicationUuid,
  params = {},
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/app-manager/document-manager/${applicationUuid}/print-documents/`,
      method: RequestMethod.POST,
      responseType: 'blob',
      params: cleanParams(params),
    })
    .then(handleFileResponse)
    .catch(handleError);

export const printAddressPage = ({ applicationUuid, version = 'v3' }) =>
  client
    .request({
      url: `${version}/app-manager/document-manager/${applicationUuid}/print-address-page/`,
      method: RequestMethod.POST,
      responseType: 'blob',
    })
    .then(handleFileResponse)
    .catch(handleError);

export const getClassYearList = ({ version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/class-years/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationResults = ({
  params = { limit: 30, offset: 0 },
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/application-results/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const generateApplicationResultsReport = ({ payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/application-result-report/`,
      method: RequestMethod.POST,
      data: payload,
      responseType: 'blob',
    })
    .then(handleFileResponse)
    .catch(handleError);

export const generateApplicationSummaryReport = ({ payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/application-summary-report/`,
      method: RequestMethod.POST,
      data: payload,
      responseType: 'blob',
    })
    .then(handleFileResponse)
    .catch(handleError);

export const generateApplicationSubmissionReport = ({
  payload,
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/application-submission-report/`,
      method: RequestMethod.POST,
      data: payload,
      responseType: 'blob',
    })
    .then(handleFileResponse)
    .catch(handleError);

export const getApplicationSubmissionStats = ({
  params = { limit: 30, offset: 0 },
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/application-submission-stats/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentFERPAStatus = ({ version = 'v2', params }) =>
  client
    .request({
      url: `${version}/app-manager/ferpa-status/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSchoolConfigurationList = ({ params = {}, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/school-configurations/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get School Configuration Details
 * @param schoolId
 * @param {string} version
 */
export const getSchoolConfigurationDetails = ({ schoolId, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/school-configurations/${schoolId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Update School Config
 * @param schoolId
 * @param data
 * @param {string} version
 */
export const updateSchoolConfigDetails = ({ schoolId, data, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/school-configurations/${schoolId}/`,
      method: RequestMethod.PATCH,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentCommonAppAccount = ({ params = {}, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/common-app/applicant/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createRequirement = ({ payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/requirements/`,
      method: RequestMethod.POST,
      data: cleanParams(payload),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateApplication = ({
  applicationUuid,
  payload,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/app-manager/applications/${applicationUuid}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteRequirement = ({ requirementId, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/requirements/${requirementId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const markRequirementsAsSent = ({
  requirementIds,
  payload,
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/requirements/mark-as-sent/`,
      method: RequestMethod.POST,
      data: payload,
      params: { ids: requirementIds },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const sendApplicationDocuments = ({
  applicationUuid,
  requirementIds: requirements,
  stage,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/app-manager/document-manager/${applicationUuid}/send-documents/`,
      method: RequestMethod.POST,
      data: { requirements },
      params: cleanParams({ stage }),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateRequirement = ({ requirementId, payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/requirements/${requirementId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateRecommendationRequest = ({
  recommendationUuid,
  payload,
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/recommendation-requests/${recommendationUuid}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteRecommendationRequest = ({
  recommendationUuid,
  payload,
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/recommendation-requests/${recommendationUuid}/decline/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCommonAppCounselorList = ({ params = {}, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/common-app/counselors/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createCommonAppForm = ({ payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/common-app/forms/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const linkStudentToCounselor = ({
  studentId,
  counselorId,
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/common-app/counselors/${counselorId}/link/`,
      method: RequestMethod.POST,
      data: { student: studentId },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const submitForm = ({ formId, formType, payload, version = 'v2' }) =>
  client
    .request({
      url: `${getFormBasePath(version, formType)}${formId}/submit/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const completeForm = ({ formId, formType, payload, version = 'v2' }) =>
  client
    .request({
      url: `${getFormBasePath(version, formType)}${formId}/complete/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const previewForm = ({ formId, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/forms/${formId}/preview/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const bulkPrintDocuments = ({ params = {}, payload, version = 'v3' }) =>
  client
    .request({
      url: `${version}/app-manager/document-manager/print-documents/`,
      method: RequestMethod.POST,
      params: cleanParams(params),
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAcceptanceVerificationList = ({
  params = {},
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/application-verifications/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAcceptanceVerificationCount = ({
  params = {},
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/application-verifications/counts/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const verifyAcceptanceLetter = ({
  payload,
  applicationUuid,
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/application-verifications/${applicationUuid}/verify/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCollegeAcceptanceVerifierList = ({ params, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/application-verifications/k12-admins/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const generateCollegeAcceptanceReport = ({ params, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/application-verifications/csv/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
      responseType: 'blob',
    })
    .then(handleFileResponse)
    .catch(handleError);

export const getStudentsHash = ({ params, version = 'v3' }) =>
  client
    .request({
      url: `${version}/app-manager/document-manager/students-hash/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const declineRequirement = ({ id, payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/requirements/${id}/decline/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const restoreRequirement = ({ id, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/requirements/${id}/restore/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteApplication = ({ applicationUuid, version = 'v3' }) =>
  client
    .request({
      url: `${version}/app-manager/applications/${applicationUuid}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPossibleRequirements = ({ uuid, params, version = 'v3' }) =>
  client
    .request({
      url: `${version}/app-manager/applications/${uuid}/possible-requirements/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationDocumentDetails = ({ id, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/application-documents/${id}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createApplicationDocument = ({ id, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/application-documents/`,
      method: RequestMethod.POST,
      data: { requirementId: id },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const uploadApplicationDocument = ({
  id,
  payload,
  params,
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/application-documents/${id}/upload-file/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const removeApplicationDocumentFile = ({ id, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/application-documents/${id}/remove-file/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const saveDocumentContent = ({ id, payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/application-documents/${id}/generate-pdf-and-save/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const saveDraftDocumentContent = ({ id, payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/application-documents/${id}/save-as-draft/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const setDocumentAsCollegeSpecific = ({ id, payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/application-documents/${id}/set-as-college-specific/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const setFormAsCollegeSpecific = ({ id, payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/forms/${id}/set-as-college-specific/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const revertToGeneralDocument = ({ id, payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/application-documents/${id}/revert-to-general/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const revertToGeneralForm = ({ id, payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/forms/${id}/revert-to-general/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRequirementDetails = ({ id, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/requirements/${id}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getFormDetails = ({ id, formType, version = 'v2' }) =>
  client
    .request({
      url: `${getFormBasePath(version, formType)}${id}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteFormAnswer = ({ id, formType, answer, version = 'v2' }) =>
  client
    .request({
      url: `${getFormBasePath(version, formType)}${id}/questions/${
        answer.question
      }/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateForm = ({ id, formType, answer, version = 'v2' }) =>
  client
    .request({
      url: `${getFormBasePath(version, formType)}${id}/`,
      method: RequestMethod.PATCH,
      data: answer,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateSchoolProfileForm = ({ id, answer, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/school-profile-forms/${id}/`,
      method: RequestMethod.PATCH,
      data: answer,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getFormData = ({ params, version = 'v2' } = {}) => {
  const formType = params?.formType;
  return client
    .request({
      url: `${getFormBasePath(version, formType)}data/`,
      method: RequestMethod.GET,
      params: getFormDataParams(params, formType),
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

export const getApplications = ({ params, version = 'v3' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/applications/`,
      method: RequestMethod.GET,
      params: { limit: 200, ...params },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentApplicationCounts = ({ params, version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/student-application-counts/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRecommendationCounts = ({ params, version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/recommendation-requests/counts/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getFerpaInfo = ({ params, version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/ferpa-waiver/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const submitFerpaWaiver = ({ params, payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/ferpa-waiver/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getFeeWaiverChecklist = ({ params, version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/fee-waiver/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const addFeeWaiverChecklist = ({
  params,
  payload,
  version = 'v2',
} = {}) =>
  client
    .request({
      url: `${version}/app-manager/fee-waiver/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateFeeWaiverChecklist = ({
  params,
  payload,
  version = 'v2',
} = {}) =>
  client
    .request({
      url: `${version}/app-manager/fee-waiver/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Fetch recommendation requests
 *
 * @param {Object} params - request query parameters
 * @param {number} params.student - student id
 * @param {string} params.application - application uuid
 * @param {string} params.recommender - recommender uuid
 * @param {string} params.subject - subject of the recommendation request
 * @param {boolean} params.schoolSpecific - true if requestType is College Specific
 * @param {string} version
 */
export const getRecommendationRequestList = ({ params, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/recommendation-requests/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Fetch recommendation requests
 *
 * @param {string} uuid - recommendation request uuid
 * @param {string} version
 */
export const getRecommendationRequest = ({ uuid, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/recommendation-requests/${uuid}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSchoolContext = ({ params, version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/application-context/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Create recommendation request
 *
 * @param {Object} params - request query parameters
 * @param {string} params.application - application uuid
 * @param {string} params.recommender - recommender uuid
 * @param {boolean} params.schoolSpecific - true if requestType is College Specific
 * @param {string} params.subject - subject of the recommendation request
 * @param {string} version
 */

export const createRecommendationRequest = ({ data, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/recommendation-requests/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Fetch recommenders
 *
 * @param {Object} params - request query parameters
 * @param {number} params.studentId - student id
 * @param {string} params.search - search text
 * @param {number} params.limit - number of recommenders to fetch
 * @param {number} params.offset - number of recommenders to skip
 * @param {string} version
 */
export const getRecommenderList = ({ params, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/recommenders/k12-admins/search/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
export const getApplicationDetails = ({ id, version = 'v3' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/applications/${id}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Create recommender
 *
 * @param {Object} body - request body
 * @param {number} body.k12Admin - selected teacher pk
 * @param {string} body.email - email
 * @param {number} body.firstName - first name
 * @param {number} body.lastName - last name
 * @param {string} version
 */
export const createRecommender = ({ data, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/recommenders/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createApplication = ({ payload, version = 'v3' }) =>
  client
    .request({
      url: `${version}/app-manager/applications/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getForm = ({ id, formType, version = 'v2' }) =>
  client
    .request({
      url: `${getFormBasePath(version, formType)}${id}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createSchoolProfileForm = ({ payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/school-profile-forms/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createCounselorProfileForm = ({ version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/counselor-profile-forms/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createTeacherProfileForm = ({ version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/teacher-profile-forms/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentConfig = ({ params, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/student-config/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createStudentConfig = ({ params, payload, version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/student-config/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateStudentConfig = ({ params, payload, version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/student-config/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCommonAppTeacher = ({ version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/common-app/teacher/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentRecommendationRequestList = ({
  params,
  version = 'v2',
} = {}) =>
  client
    .request({
      url: `${version}/app-manager/recommendation-requests/students/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const submitRecommendationRequest = ({ uuid, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/recommendation-requests/${uuid}/submit/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const declineRecommendationRequest = ({
  uuid,
  payload,
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/recommendation-requests/${uuid}/decline/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const restoreRecommendationRequest = ({ uuid, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/recommendation-requests/${uuid}/restore/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const triggerCommonAppSync = ({
  version = 'v2',
  params,
  payload,
} = {}) =>
  client
    .request({
      url: `${version}/app-manager/common-app/sync/`,
      method: RequestMethod.POST,
      params,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAcceptanceLetterList = ({ params, version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/acceptance-letters/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/** Get hash of non senior students who don't have CAM access  */
export const getNonSeniorAccessHash = ({ version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/non-senior-access/hash/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/** Update Non Senior Access list */
export const updateNonSeniorAccess = ({ version = 'v2', data }) =>
  client
    .request({
      url: `${version}/app-manager/non-senior-access/assign/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/** Get count of non senior students who have CAM access */
export const getNonSeniorAccessCount = ({ version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/non-senior-access/count/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/** Mark all student requirements as completed */
export const markAllRequirementsComplete = ({ uuid, version = 'v3' }) =>
  client
    .request({
      url: `${version}/app-manager/applications/${uuid}/mark-student-requirements-as-completed/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationSubmissionCounts = ({
  params,
  version = 'v2',
} = {}) =>
  client
    .request({
      url: `${version}/app-manager/application-submission-stats/counts/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationSubmissionStudentsHash = ({
  params,
  version = 'v2',
} = {}) =>
  client
    .request({
      url: `${version}/app-manager/application-submission-stats/student-hash/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCommonAppFerpaURL = ({ version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/common-app/applicant/ferpa-url/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationResultsCounts = ({ params, version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/application-results/counts/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationResultsStudentsHash = ({
  params,
  version = 'v2',
} = {}) =>
  client
    .request({
      url: `${version}/app-manager/application-results/student-hash/`,
      method: RequestMethod.GET,
      params: cleanParams(params),
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const declineAcceptanceLetter = ({
  payload,
  applicationUuid,
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/application-verifications/${applicationUuid}/decline/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteCommonAppApplicant = ({ params, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/common-app/applicant/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const addAcceptanceLetter = ({ payload, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/acceptance-letters/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteAcceptanceLetter = ({ fileId, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/acceptance-letters/${fileId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationVerificationDetails = ({
  applicationUuid,
  version = 'v2',
}) =>
  client
    .request({
      url: `${version}/app-manager/application-verifications/${applicationUuid}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationShortCode = ({ shortCode, version = 'v3' }) =>
  client
    .request({
      url: `${version}/app-manager/applications/short-code/${shortCode}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPresetRequirements = ({ version = 'v2' } = {}) =>
  client
    .request({
      url: `${version}/app-manager/preset-requirements/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateSchoolApplication = ({
  version = 'v3',
  payload,
  appId,
} = {}) =>
  client
    .request({
      url: `${version}/app-manager/applications/${appId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationCollegeResults = ({ version = 'v2', params }) =>
  client
    .request({
      url: `${version}/app-manager/application-college-results/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationCollegeResultsCount = ({ version = 'v2', params }) =>
  client
    .request({
      url: `${version}/app-manager/application-college-results/counts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationCollegeResultsStudentList = ({
  version = 'v2',
  params,
}) =>
  client
    .request({
      url: `${version}/app-manager/application-student-results/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationCollegeResultsStudentsHash = ({
  version = 'v2',
  params,
}) =>
  client
    .request({
      url: `${version}/app-manager/application-student-results/student-hash/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const downloadApplicationCollegeResultsStudentListCSV = ({
  version = 'v2',
  params,
}) =>
  client
    .request({
      url: `${version}/app-manager/application-student-results/student-list-csv/`,
      method: RequestMethod.POST,
      params,
      responseType: 'blob',
    })
    .then(handleFileResponse)
    .catch(handleError);

/**
 * To update student's enrollment decision in bulk
 * @param {string} payload.studentsHash - student's hash
 * @param {number} collegeId - college id to which the student belongs to
 */
export const bulkUpdateEnrollmentDecision = ({
  version = 'v2',
  payload,
  collegeId,
}) =>
  client
    .request({
      url: `${version}/app-manager/application-college-results/${collegeId}/enroll_students/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const downloadCollegeListCountCsv = ({ version = 'v2', params }) =>
  client
    .request({
      url: `${version}/app-manager/application-college-results/college-list-count-csv/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createFileAnswer = ({ version = 'v2', data }) =>
  client
    .request({
      url: `${version}/app-manager/form-file-answers/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getApplicationDocumentSendRequests = ({
  params,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/app-manager/application-document-send-requests/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * To update student's application status in bulk
 * @param {string} payload.studentsHash - student's hash
 * @param {number} payload.status - application status
 * @param {number} collegeId - college id to which the student belongs to
 */
export const bulkUpdateAppStatus = ({ version = 'v2', payload, collegeId }) =>
  client
    .request({
      url: `${version}/app-manager/application-college-results/${collegeId}/update-status-in-bulk/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * @param {number} id pk of the requirement that'd be waived
 * @param {Object<{note: string}>} payload note sent within waiving
 * @returns @func(waiveRequirement):void
 */
export const waiveRequirement = ({ id, version = 'v2', payload }) =>
  client
    .request({
      url: `${version}/app-manager/requirements/${id}/waive/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCommonAppHandoffData = ({ type, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/common-app/url-handoffs/${type}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSchoolProfileFormsData = ({ version = 'v2', params }) =>
  client
    .request({
      url: `${version}/app-manager/school-profile-forms/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12SchoolList = ({ version = 'v2', schoolId, params }) => {
  const url = schoolId
    ? `${version}/app-manager/k12-schools/${schoolId}`
    : `${version}/app-manager/k12-schools/`;

  return client
    .request({
      url,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

export const getGuardianFERPAWaiverStatuses = ({ params, version = 'v2' }) =>
  client
    .request({
      url: `${version}/app-manager/guardian-ferpa-waiver/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
